import React, { useCallback, useEffect, useState } from 'react';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Search, Building, UserPlus, Globe, MapPin, Users2, Briefcase } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { OrganizationModal } from '../components/crm/OrganizationModal';
import { InvitePlatformModal } from '../components/crm/InvitePlatformModal';

export default function SalesCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orgModal, setOrgModal] = useState(false);
  const [invite, setInvite] = useState({ open: false, orgId: '' });

  const load = useCallback(async () => {
    const res = await crmFetch('/organizations');
    if (res.success) setCompanies(res.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = companies.filter((c) => {
    if (!q) return true;
    return [c.name, c.city, c.industry].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <SalesLayout>
      <div data-testid="sales-companies-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building className="w-6 h-6 text-blue-600" /> Companies
            </h1>
            <p className="text-sm text-gray-500 mt-1">Client companies onboarded onto the JobastiIQ platform.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setInvite({ open: true, orgId: '' })} data-testid="companies-invite-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite User
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOrgModal(true)} data-testid="companies-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </div>
        </div>

        <div className="relative flex-1 max-w-sm mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input className="pl-9" placeholder="Search companies…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="companies-search-input" />
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-16">Loading companies…</p>
        ) : filtered.length === 0 ? (
          <Card data-testid="companies-empty">
            <CardContent className="p-10 text-center">
              <Building className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No companies yet</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">Onboard your first client company to the platform.</p>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setOrgModal(true)} data-testid="companies-empty-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Company
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="companies-table">
                  <thead>
                    <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">City</th>
                      <th className="px-4 py-3 font-medium">Industry</th>
                      <th className="px-4 py-3 font-medium text-center">Open Roles</th>
                      <th className="px-4 py-3 font-medium text-center">Members</th>
                      <th className="px-4 py-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => (
                      <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50/60" data-testid={`company-row-${c.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{c.name}</div>
                          {c.website && (
                            <a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
                              className="text-xs text-blue-500 hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Globe className="w-3 h-3" /> {c.website}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {c.city ? <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /> {c.city}</span> : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.industry || '—'}</td>
                        <td className="px-4 py-3 text-center text-gray-600"><span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3 text-gray-400" />{c.openPositions}</span></td>
                        <td className="px-4 py-3 text-center text-gray-600"><span className="inline-flex items-center gap-1"><Users2 className="w-3 h-3 text-gray-400" />{c.memberCount}</span></td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => setInvite({ open: true, orgId: c.id })} data-testid={`company-invite-${c.id}`}>
                            <UserPlus className="w-4 h-4 mr-1" /> Invite
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <OrganizationModal open={orgModal} onOpenChange={setOrgModal} onSaved={load} />
      <InvitePlatformModal open={invite.open} onOpenChange={(o) => setInvite((s) => ({ ...s, open: o }))} defaultOrgId={invite.orgId} onInvited={load} />
    </SalesLayout>
  );
}
